import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "utils/apiClient";
import paths from "routes/paths";

const TASKS_BASE = "/olitask-service/api/v1/tasks";
const USERS_BASE = "/identity-service/api/v1/users";
const PROFILE_USERS_BASE = "/profile-service/api/v1/user-profiles";

const EMPTY_YEAR = new Date().getFullYear();
const EMPTY_TOTAL_SALES = {
  [EMPTY_YEAR]: Array.from({ length: 12 }, () =>
    Array.from({ length: 12 }, () => 0),
  ),
};

const STATUS_COLORS = {
  COMPLETED: "success",
  CANCELLED: "danger",
  EXPIRED: "secondary",
};

const MARKET_SHARE_STATUSES = [
  { keys: ["COMPLETED"], name: "Completed", color: STATUS_COLORS.COMPLETED },
  { keys: ["CANCELLED"], name: "Cancelled", color: STATUS_COLORS.CANCELLED },
  { keys: ["EXPIRED"], name: "Expired", color: STATUS_COLORS.EXPIRED },
];

const PAGE_SIZE = 500;
const STATUS_COUNTS_TTL_MS = 10 * 60 * 1000;
const ALL_STATUSES = [
  "NEW",
  "PENDING",
  "ACCEPTED",
  "NAVIGATING",
  "IN_PROGRESS",
  "ARRIVED",
  "IN_REVIEW",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
  "DEACTIVATED",
  "DELETED",
  "FLAGGED",
  "BLOCKED",
];

const getPage = (payload) => {
  const normalize = (value) => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) return value;
    // Common API envelope patterns: { data: {...} }, { result: {...} }
    if (value.data && typeof value.data === "object") return value.data;
    if (value.result && typeof value.result === "object") return value.result;
    return value;
  };

  const p = normalize(payload);

  if (Array.isArray(p)) {
    return {
      content: p,
      totalElements: p.length,
    };
  }

  if (Array.isArray(p?.content)) {
    return {
      content: p.content,
      totalElements:
        typeof p.totalElements === "number"
          ? p.totalElements
          : p.content.length,
    };
  }

  // Another common pattern: { data: { content: [...] } }
  if (Array.isArray(p?.data?.content)) {
    const content = p.data.content;
    return {
      content,
      totalElements:
        typeof p.data.totalElements === "number"
          ? p.data.totalElements
          : content.length,
    };
  }

  // Pattern: { data: [...] }
  if (Array.isArray(p?.data)) {
    const content = p.data;
    return { content, totalElements: content.length };
  }

  return {
    content: [],
    totalElements: 0,
  };
};

const mergeTimestampsFromIdentity = (identityUsers = [], profileUsers = []) => {
  const map = new Map();
  identityUsers.forEach((u) => {
    if (!u?.id) return;
    map.set(u.id, u);
  });

  return (profileUsers || []).map((u) => {
    const identity = map.get(u?.id);
    if (!identity) return u;
    return {
      ...u,
      createdAt:
        identity.createdAt ||
        identity.createdOn ||
        identity.insertedAt ||
        u.createdAt ||
        u.createdOn ||
        u.acceptedAt ||
        null,
      updatedAt:
        identity.updatedAt ||
        identity.updatedOn ||
        identity.modifiedAt ||
        identity.lastUpdatedAt ||
        u.updatedAt ||
        u.updatedOn ||
        null,
    };
  });
};

const compactCount = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const calculateTrend = (current, previous) => {
  if (!previous && !current) return { label: "0%", up: true };
  if (!previous) return { label: "+100%", up: true };

  const delta = ((current - previous) / previous) * 100;
  const normalized = Number.isFinite(delta) ? delta : 0;
  const sign = normalized >= 0 ? "+" : "";
  return {
    label: `${sign}${normalized.toFixed(1)}%`,
    up: normalized >= 0,
  };
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "object") {
    const millis =
      (typeof value.epochMillis === "number" && value.epochMillis) ||
      (typeof value.millis === "number" && value.millis) ||
      (typeof value.timestamp === "number" && value.timestamp) ||
      null;
    if (millis) {
      const d = new Date(millis);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Human-friendly variants, e.g. "Dec 19,2025, 6:07pm"
    const humanMonthTime = trimmed.match(
      /^([A-Za-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s*(\d{4})\s*,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]m)?$/i,
    );
    if (humanMonthTime) {
      const [, mon, dd, yyyy, hh, mm, ss, ap] = humanMonthTime;
      const hasAmPm = Boolean(ap);
      const normalizedHuman = `${mon} ${dd}, ${yyyy} ${hh}:${mm}${ss ? `:${ss}` : ""}${hasAmPm ? ` ${ap.toUpperCase()}` : ""}`;
      const d = new Date(normalizedHuman);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Common backend format: "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
    const normalized = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(trimmed)
      ? trimmed.replace(" ", "T")
      : trimmed;

    // "DD-MM-YYYY" or "DD/MM/YYYY"
    const dmy = normalized.match(/^(\d{2})[-/](\d{2})[-/](\d{4})(?:\s|T|$)(.*)$/);
    if (dmy) {
      const [, dd, mm, yyyy, rest] = dmy;
      const iso = `${yyyy}-${mm}-${dd}${rest ? `T${rest.replace(/^T/, "")}` : ""}`;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const buildWeeklySeries = (tasks) => {
  const now = new Date();
  const last7Start = new Date(now);
  last7Start.setDate(now.getDate() - 6);
  last7Start.setHours(0, 0, 0, 0);

  const prev7Start = new Date(last7Start);
  prev7Start.setDate(last7Start.getDate() - 7);

  const series = Array.from({ length: 7 }, () => 0);
  let currentWeekCount = 0;
  let previousWeekCount = 0;

  tasks.forEach((task) => {
    const createdAt = parseDate(task?.createdAt);
    if (!createdAt) return;

    if (createdAt >= last7Start) {
      series[createdAt.getDay()] += 1;
      currentWeekCount += 1;
      return;
    }

    if (createdAt >= prev7Start && createdAt < last7Start) {
      previousWeekCount += 1;
    }
  });

  return {
    series,
    ...calculateTrend(currentWeekCount, previousWeekCount),
  };
};

const buildUserSeries = (users) => {
  const now = new Date();
  const series = [0, 0, 0, 0];
  let current28Count = 0;
  let previous28Count = 0;

  const getUserTimestamp = (user) =>
    parseDate(
      user?.createdAt ||
        user?.createdOn ||
        user?.created_at ||
        user?.created ||
        user?.dateCreated ||
        user?.acceptedAt ||
        user?.securityPhotoVerification?.uploadedOn ||
        user?.securityPhotoVerification?.uploadedAt,
    );

  users.forEach((user) => {
    const createdAt = getUserTimestamp(user);
    if (!createdAt) return;

    const diffDays = Math.floor((now - createdAt) / (24 * 60 * 60 * 1000));
    if (diffDays < 0) return;

    if (diffDays < 28) {
      const weekBucket = Math.min(3, Math.floor(diffDays / 7));
      series[3 - weekBucket] += 1;
      current28Count += 1;
      return;
    }

    if (diffDays < 56) {
      previous28Count += 1;
    }
  });

  return {
    series,
    ...calculateTrend(current28Count, previous28Count),
  };
};

const formatShortDay = (date) =>
  date.toLocaleDateString(undefined, { weekday: "short" });

const formatMonthDay = (date) =>
  date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const formatShortMonth = (date) =>
  date.toLocaleDateString(undefined, { month: "short" });

const buildUserGrowthRanges = (users) => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const getUserTimestamp = (user) =>
    parseDate(
      user?.createdAt ||
        user?.createdOn ||
        user?.created_at ||
        user?.created ||
        user?.dateCreated ||
        user?.acceptedAt ||
        user?.securityPhotoVerification?.uploadedOn ||
        user?.securityPhotoVerification?.uploadedAt,
    );

  const buildDaily = ({ days, labelFn }) => {
    const start = new Date(startOfToday);
    start.setDate(start.getDate() - (days - 1));

    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - days);

    const labels = Array.from({ length: days }, (_, index) => {
      const d = new Date(start);
      d.setDate(start.getDate() + index);
      return labelFn(d);
    });

    const series = Array.from({ length: days }, () => 0);
    const previousSeries = Array.from({ length: days }, () => 0);
    let currentCount = 0;
    let previousCount = 0;

    users.forEach((user) => {
      const createdAt = getUserTimestamp(user);
      if (!createdAt) return;
      if (createdAt > end) return;

      if (createdAt >= start) {
        const dayIndex = Math.floor(
          (createdAt.getTime() - start.getTime()) / (24 * 60 * 60 * 1000),
        );
        if (dayIndex >= 0 && dayIndex < days) {
          series[dayIndex] += 1;
          currentCount += 1;
        }
        return;
      }

      if (createdAt >= prevStart && createdAt < start) {
        const dayIndex = Math.floor(
          (createdAt.getTime() - prevStart.getTime()) / (24 * 60 * 60 * 1000),
        );
        if (dayIndex >= 0 && dayIndex < days) {
          previousSeries[dayIndex] += 1;
          previousCount += 1;
        }
      }
    });

    return {
      labels,
      series,
      previousSeries,
      currentTotal: currentCount,
      previousTotal: previousCount,
      ...calculateTrend(currentCount, previousCount),
    };
  };

  const buildWeeklyBuckets = ({ weeks }) => {
    const days = weeks * 7;
    const start = new Date(startOfToday);
    start.setDate(start.getDate() - (days - 1));

    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - days);

    const labels = Array.from({ length: weeks }, (_, index) => `W${index + 1}`);
    const series = Array.from({ length: weeks }, () => 0);
    const previousSeries = Array.from({ length: weeks }, () => 0);
    let currentCount = 0;
    let previousCount = 0;

    users.forEach((user) => {
      const createdAt = getUserTimestamp(user);
      if (!createdAt) return;
      if (createdAt > end) return;

      if (createdAt >= start) {
        const diffDays = Math.floor(
          (createdAt.getTime() - start.getTime()) / (24 * 60 * 60 * 1000),
        );
        if (diffDays >= 0 && diffDays < days) {
          const bucket = Math.min(weeks - 1, Math.floor(diffDays / 7));
          series[bucket] += 1;
          currentCount += 1;
        }
        return;
      }

      if (createdAt >= prevStart && createdAt < start) {
        const diffDays = Math.floor(
          (createdAt.getTime() - prevStart.getTime()) / (24 * 60 * 60 * 1000),
        );
        if (diffDays >= 0 && diffDays < days) {
          const bucket = Math.min(weeks - 1, Math.floor(diffDays / 7));
          previousSeries[bucket] += 1;
          previousCount += 1;
        }
      }
    });

    return {
      labels,
      series,
      previousSeries,
      currentTotal: currentCount,
      previousTotal: previousCount,
      ...calculateTrend(currentCount, previousCount),
    };
  };

  const buildMonthlyBuckets = ({ months }) => {
    const start = new Date(now);
    start.setMonth(start.getMonth() - (months - 1));
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const prevStart = new Date(start);
    prevStart.setMonth(prevStart.getMonth() - months);

    const labels = Array.from({ length: months }, (_, index) => {
      const d = new Date(start);
      d.setMonth(start.getMonth() + index);
      return formatShortMonth(d);
    });

    const series = Array.from({ length: months }, () => 0);
    const previousSeries = Array.from({ length: months }, () => 0);
    let currentCount = 0;
    let previousCount = 0;

    users.forEach((user) => {
      const createdAt = getUserTimestamp(user);
      if (!createdAt) return;
      if (createdAt > end) return;

      if (createdAt >= start) {
        const monthIndex =
          (createdAt.getFullYear() - start.getFullYear()) * 12 +
          (createdAt.getMonth() - start.getMonth());
        if (monthIndex >= 0 && monthIndex < months) {
          series[monthIndex] += 1;
          currentCount += 1;
        }
        return;
      }

      if (createdAt >= prevStart && createdAt < start) {
        const monthIndex =
          (createdAt.getFullYear() - prevStart.getFullYear()) * 12 +
          (createdAt.getMonth() - prevStart.getMonth());
        if (monthIndex >= 0 && monthIndex < months) {
          previousSeries[monthIndex] += 1;
          previousCount += 1;
        }
      }
    });

    return {
      labels,
      series,
      previousSeries,
      currentTotal: currentCount,
      previousTotal: previousCount,
      ...calculateTrend(currentCount, previousCount),
    };
  };

  return {
    last7Days: buildDaily({ days: 7, labelFn: formatMonthDay }),
    lastMonth: buildWeeklyBuckets({ weeks: 4 }),
    lastYear: buildMonthlyBuckets({ months: 12 }),
  };
};

const buildSubmittedSeriesByYear = (tasks) => {
  const yearMap = {};

  tasks.forEach((task) => {
    const submittedAt = parseDate(task?.updatedAt || task?.createdAt);
    if (!submittedAt) return;

    const year = submittedAt.getFullYear();
    const monthIndex = submittedAt.getMonth();
    const day = submittedAt.getDate();
    const dayBucket = Math.min(11, Math.floor((day - 1) / 3));

    if (!yearMap[year]) {
      yearMap[year] = Array.from({ length: 12 }, () =>
        Array.from({ length: 12 }, () => 0),
      );
    }

    yearMap[year][monthIndex][dayBucket] += 1;
  });

  const years = Object.keys(yearMap)
    .map((y) => Number(y))
    .sort((a, b) => b - a);

  if (!years.length) {
    return {
      years: [EMPTY_YEAR],
      dataByYear: EMPTY_TOTAL_SALES,
    };
  }

  return {
    years,
    dataByYear: yearMap,
  };
};

const toPct = (num, den) => {
  if (!den) return "0%";
  return `${((num * 100) / den).toFixed(1)}%`;
};

const createEmptyStatusCounts = () =>
  ALL_STATUSES.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});

export default function useHomeDashboardData({ scope = "full" } = {}) {
  const isAnalyticsScope = scope === "analytics";
  const latestFetchIdRef = useRef(0);
  const inFlightDashboardFetchRef = useRef(null);
  const statusCountsCacheRef = useRef(null);
  const statusCountsFetchedAtRef = useRef(0);
  const statusCountsInFlightRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState({
    weeklySalesData: Array.from({ length: 7 }, () => 0),
    weeklySalesTotal: 0,
    weeklySalesTrendLabel: "0%",
    weeklySalesTrendUp: true,
    totalOrderData: [0, 0, 0, 0],
    totalOrderTotal: 0,
    totalOrderTrendLabel: "0%",
    totalOrderTrendUp: true,
    totalSalesByYear: EMPTY_TOTAL_SALES,
    submittedYears: [EMPTY_YEAR],
    marketShareData: [
      ...MARKET_SHARE_STATUSES.map((status, index) => ({
        id: index + 1,
        value: 0,
        name: status.name,
        color: status.color,
      })),
    ],
    statusCounts: {},
    healthMetrics: {
      completionRate: "0%",
      interruptionRate: "0%",
      inFlightRate: "0%",
      completedCount: 0,
      interruptedCount: 0,
      inFlightCount: 0,
      totalTracked: 0,
    },
    usersForAnalytics: [],
    usersCustomersForAnalytics: [],
    usersStaffForAnalytics: [],
    userGrowthRanges: null,
    userGrowthRangesCustomers: null,
    userGrowthRangesStaff: null,
    userGrowthRangesUnclassified: null,
    usersUnclassifiedForAnalytics: [],
  });

  const fetchAllPages = useCallback(async (urlBase, { pageSize = 500, maxPages = 20 } = {}) => {
    const all = [];
    let page = 0;
    let totalPages = 1;

    while (page < totalPages && page < maxPages) {
      const res = await apiClient.get(`${urlBase}?page=${page}&size=${pageSize}`);
      const payload = res?.data || {};
      const content = Array.isArray(payload?.content) ? payload.content : [];
      totalPages = Number(payload?.totalPages || 1);
      all.push(...content);
      page += 1;
      if (!content.length) break;
    }

    return all;
  }, []);

  const fetchStatusCounts = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    const hasFreshCache =
      statusCountsCacheRef.current &&
      now - statusCountsFetchedAtRef.current < STATUS_COUNTS_TTL_MS;

    if (!force && hasFreshCache) {
      return statusCountsCacheRef.current;
    }

    if (statusCountsInFlightRef.current) {
      return statusCountsInFlightRef.current;
    }

    const request = (async () => {
      try {
        const statusRequests = ALL_STATUSES.map((status) =>
          apiClient.get(`${TASKS_BASE}/filter?status=${status}&page=0&size=1`),
        );
        const statusResults = await Promise.allSettled(statusRequests);
        const nextStatusCounts = createEmptyStatusCounts();

        ALL_STATUSES.forEach((status, index) => {
          const result = statusResults[index];
          if (result?.status === "fulfilled") {
            nextStatusCounts[status] = getPage(result.value?.data).totalElements;
          }
        });

        statusCountsCacheRef.current = nextStatusCounts;
        statusCountsFetchedAtRef.current = Date.now();
        return nextStatusCounts;
      } catch (error) {
        console.error("Failed to fetch dashboard status counts:", error);
        return statusCountsCacheRef.current || createEmptyStatusCounts();
      }
    })();

    statusCountsInFlightRef.current = request;

    try {
      return await request;
    } finally {
      if (statusCountsInFlightRef.current === request) {
        statusCountsInFlightRef.current = null;
      }
    }
  }, []);

  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    const token = sessionStorage.getItem("authToken");
    if (!token) return;

    if (inFlightDashboardFetchRef.current) {
      return inFlightDashboardFetchRef.current;
    }

    const runFetch = (async () => {
      const fetchId = ++latestFetchIdRef.current;
      setSyncing(true);
      if (!silent) setLoading(true);

      try {
        if (isAnalyticsScope) {
          // Analytics route only needs user-series data; skip tasks/projects/status-count requests.
          const [identityAllUsers, profileCustomersUsers, profileStaffUsers] = await Promise.all([
            fetchAllPages(USERS_BASE, { pageSize: 500, maxPages: 20 }),
            (async () => {
              const res = await apiClient.get(
                `${PROFILE_USERS_BASE}?page=0&size=${PAGE_SIZE}&refresh=true&staff=false`,
              );
              return getPage(res?.data).content;
            })(),
            (async () => {
              const res = await apiClient.get(
                `${PROFILE_USERS_BASE}?page=0&size=${PAGE_SIZE}&refresh=true&staff=true`,
              );
              return getPage(res?.data).content;
            })(),
          ]);

          const customersUsersEnriched = mergeTimestampsFromIdentity(
            identityAllUsers,
            profileCustomersUsers,
          );
          const staffUsersEnriched = mergeTimestampsFromIdentity(
            identityAllUsers,
            profileStaffUsers,
          );
          const classifiedIds = new Set(
            [...customersUsersEnriched, ...staffUsersEnriched]
              .map((u) => u?.id)
              .filter(Boolean),
          );
          const unclassifiedUsersEnriched = (identityAllUsers || []).filter(
            (u) => u?.id && !classifiedIds.has(u.id),
          );

          const userGrowthRanges = buildUserGrowthRanges(identityAllUsers);
          const userGrowthRangesCustomers = buildUserGrowthRanges(customersUsersEnriched);
          const userGrowthRangesStaff = buildUserGrowthRanges(staffUsersEnriched);
          const userGrowthRangesUnclassified = buildUserGrowthRanges(
            unclassifiedUsersEnriched,
          );

          if (fetchId !== latestFetchIdRef.current) return;

          setData((prev) => ({
            ...prev,
            totalOrderTotal: identityAllUsers.length,
            totalOrderData: buildUserSeries(identityAllUsers).series,
            usersForAnalytics: identityAllUsers,
            usersCustomersForAnalytics: customersUsersEnriched,
            usersStaffForAnalytics: staffUsersEnriched,
            usersUnclassifiedForAnalytics: unclassifiedUsersEnriched,
            userGrowthRanges,
            userGrowthRangesCustomers,
            userGrowthRangesStaff,
            userGrowthRangesUnclassified,
          }));

          return;
        }

        const [baseResults, statusCounts] = await Promise.all([
          Promise.allSettled([
            apiClient.get(`${TASKS_BASE}?page=0&size=${PAGE_SIZE}`),
            apiClient.get(`${USERS_BASE}?page=0&size=${PAGE_SIZE}`),
          ]),
          fetchStatusCounts({ force: !silent }),
        ]);

        const [tasksResult, usersDetailsResult] = baseResults;
        const tasksPage = getPage(tasksResult.value?.data);
        const usersDetailsPage = getPage(usersDetailsResult.value?.data);

        // Build Staff vs Customer segmentation using profile-service filters,
        // then join identity-service `createdAt` timestamps for growth computations.
        const [identityAllUsers, profileCustomersUsers, profileStaffUsers] = await Promise.all([
          fetchAllPages(USERS_BASE, { pageSize: 500, maxPages: 20 }),
          (async () => {
            const res = await apiClient.get(
              `${PROFILE_USERS_BASE}?page=0&size=${PAGE_SIZE}&refresh=true&staff=false`,
            );
            return getPage(res?.data).content;
          })(),
          (async () => {
            const res = await apiClient.get(
              `${PROFILE_USERS_BASE}?page=0&size=${PAGE_SIZE}&refresh=true&staff=true`,
            );
            return getPage(res?.data).content;
          })(),
        ]);

        const customersUsersEnriched = mergeTimestampsFromIdentity(
          identityAllUsers,
          profileCustomersUsers,
        );
        const staffUsersEnriched = mergeTimestampsFromIdentity(
          identityAllUsers,
          profileStaffUsers,
        );
        const classifiedIds = new Set(
          [...customersUsersEnriched, ...staffUsersEnriched]
            .map((u) => u?.id)
            .filter(Boolean),
        );
        const unclassifiedUsersEnriched = (identityAllUsers || []).filter(
          (u) => u?.id && !classifiedIds.has(u.id),
        );
        const allUsersEnriched = usersDetailsPage.content.length
          ? mergeTimestampsFromIdentity(identityAllUsers, usersDetailsPage.content)
          : identityAllUsers;

        const weekly = buildWeeklySeries(tasksPage.content);
        const userStats = buildUserSeries(allUsersEnriched);
        const userGrowthRanges = buildUserGrowthRanges(allUsersEnriched);
        const userGrowthRangesCustomers = buildUserGrowthRanges(customersUsersEnriched);
        const userGrowthRangesStaff = buildUserGrowthRanges(staffUsersEnriched);
        const userGrowthRangesUnclassified = buildUserGrowthRanges(
          unclassifiedUsersEnriched,
        );
        const submittedTasks = tasksPage.content.filter((task) => {
          const status = String(task?.status || "").toUpperCase();
          return status === "IN_REVIEW" || status === "COMPLETED";
        });
        const submittedSeries = buildSubmittedSeriesByYear(submittedTasks);
        const completed = statusCounts.COMPLETED || 0;
        const interrupted =
          (statusCounts.CANCELLED || 0) +
          (statusCounts.EXPIRED || 0) +
          (statusCounts.DEACTIVATED || 0) +
          (statusCounts.DELETED || 0);
        const trackedTotal = Object.values(statusCounts).reduce(
          (acc, value) => acc + (Number(value) || 0),
          0,
        );
        const inFlight = Math.max(0, trackedTotal - completed - interrupted);

        if (fetchId !== latestFetchIdRef.current) return;

        setData({
          weeklySalesData: weekly.series,
          weeklySalesTotal: tasksPage.totalElements,
          weeklySalesTrendLabel: weekly.label,
          weeklySalesTrendUp: weekly.up,

          totalOrderData: userStats.series,
          totalOrderTotal: usersDetailsPage.totalElements,
          totalOrderTrendLabel: userStats.label,
          totalOrderTrendUp: userStats.up,
          userGrowthRanges,
          userGrowthRangesCustomers,
          userGrowthRangesStaff,
          userGrowthRangesUnclassified,
          usersForAnalytics: allUsersEnriched,
          usersCustomersForAnalytics: customersUsersEnriched,
          usersStaffForAnalytics: staffUsersEnriched,
          usersUnclassifiedForAnalytics: unclassifiedUsersEnriched,

          totalSalesByYear: submittedSeries.dataByYear,
          submittedYears: submittedSeries.years,

          marketShareData: MARKET_SHARE_STATUSES.map((status, index) => ({
            id: index + 1,
            value: status.keys.reduce(
              (acc, key) => acc + (statusCounts[key] || 0),
              0,
            ),
            name: status.name,
            color: status.color,
          })),

          statusCounts,
          healthMetrics: {
            completionRate: toPct(completed, trackedTotal),
            interruptionRate: toPct(interrupted, trackedTotal),
            inFlightRate: toPct(inFlight, trackedTotal),
            completedCount: completed,
            interruptedCount: interrupted,
            inFlightCount: inFlight,
            totalTracked: trackedTotal,
          },
        });
      } catch (error) {
        console.error("Failed to fetch home dashboard data:", error);
      } finally {
        if (!silent && fetchId === latestFetchIdRef.current) {
          setLoading(false);
        }
        if (fetchId === latestFetchIdRef.current) {
          setSyncing(false);
        }
      }
    })();

    inFlightDashboardFetchRef.current = runFetch;

    try {
      return await runFetch;
    } finally {
      if (inFlightDashboardFetchRef.current === runFetch) {
        inFlightDashboardFetchRef.current = null;
      }
    }
  }, [fetchAllPages, fetchStatusCounts, isAnalyticsScope]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    // Avoid background polling on the analytics route to reduce network chatter.
    if (isAnalyticsScope) return undefined;

    const refresh = () => fetchDashboardData({ silent: true });

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);

    const interval = setInterval(refresh, 60 * 1000);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [fetchDashboardData, isAnalyticsScope]);

  return useMemo(
    () => ({
      loading,
      syncing,
      refetch: fetchDashboardData,
      weeklySalesData: data.weeklySalesData,
      weeklySalesTotalLabel: compactCount(data.weeklySalesTotal),
      weeklySalesTrendLabel: data.weeklySalesTrendLabel,
      weeklySalesTrendUp: data.weeklySalesTrendUp,
      weeklySalesLink: paths.tasks,

      totalOrderData: data.totalOrderData,
      totalOrderTotalLabel: compactCount(data.totalOrderTotal),
	      totalOrderTrendLabel: data.totalOrderTrendLabel,
      totalOrderTrendUp: data.totalOrderTrendUp,
      totalOrderLink: paths.users,
      userGrowthRanges: data.userGrowthRanges,
      usersForAnalytics: data.usersForAnalytics,
      userGrowthRangesCustomers: data.userGrowthRangesCustomers,
      userGrowthRangesStaff: data.userGrowthRangesStaff,
      userGrowthRangesUnclassified: data.userGrowthRangesUnclassified,
      usersCustomersForAnalytics: data.usersCustomersForAnalytics,
      usersStaffForAnalytics: data.usersStaffForAnalytics,
      usersUnclassifiedForAnalytics: data.usersUnclassifiedForAnalytics,

      totalSalesByYear: data.totalSalesByYear,
      submittedYears: data.submittedYears,
      totalSalesLink: paths.tasks,
      marketShareData: data.marketShareData,
      marketShareLink: paths.tasks,
      statusCounts: data.statusCounts,
      healthMetrics: data.healthMetrics,
    }),
    [data, fetchDashboardData, loading, syncing],
  );
}
